import React, { useState } from 'react';
import { Search, ChevronRight, ChevronDown } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';

const faqCategories = [
  { icon: '📊', name: 'Accounts Receivable' },
  { icon: '🏥', name: 'Admission' },
  { icon: '🏠', name: 'Home Health Aide' },
  { icon: '📋', name: 'Clinical' },
  { icon: '✈️', name: 'EVV' },
  { icon: '📝', name: 'Audit Trail' },
  { icon: '⭐', name: 'CMS' },
  { icon: '💰', name: 'Billing' },
  { icon: '💻', name: 'EHR' },
  { icon: '🔗', name: 'CareConnect' },
  { icon: '✈️', name: 'HHAExchange' },
  { icon: '📊', name: 'Dashboard' },
  { icon: '✈️', name: 'Indiana EVV' },
  { icon: '✅', name: 'Eligibility' },
  { icon: '🔑', name: 'Login' },
  { icon: '👥', name: 'Human Resources' },
  { icon: '📄', name: 'OASIS' },
  { icon: '🏥', name: 'Insurance' },
  { icon: '📦', name: 'Orders' },
  { icon: '💊', name: 'Medication' },
  { icon: '📋', name: 'Plan of Care' },
  { icon: '📴', name: 'Offline' },
  { icon: '📊', name: 'Reports' },
  { icon: '💵', name: 'Payroll' },
  { icon: '📅', name: 'Schedule' },
  { icon: '🔐', name: 'Prior Auth' },
  { icon: '📞', name: 'Telephony' },
  { icon: '📍', name: 'Sandata' },
  { icon: '👤', name: 'User/Security Manager' },
  { icon: '⚙️', name: 'Setup' },
  { icon: '⭐', name: 'General' },
  { icon: '📍', name: 'Tellus' },
  { icon: '🏥', name: 'Hospice' },
  { icon: '📍', name: 'VA EVV' },
  { icon: '💰', name: 'Hospice Billing' },
  { icon: '🩺', name: 'Therapy' },
  { icon: '📄', name: 'Document Templates' },
  { icon: '🔧', name: 'Tools' },
  { icon: '📱', name: 'IvoryPlus App' },
  { icon: '💬', name: 'Open Shift Notification' },
  { icon: '📋', name: 'Notice of Admission (NOA)' },
  { icon: '📄', name: 'Custom Docs' },
];

const topFAQs = [
  { id: 1, question: 'How can I find out who deleted visits?' },
  { id: 2, question: 'How do I document interventions in the SNAP note?' },
  { id: 3, question: "Is there a way to capture a staff member's Hire Date and Start Date?" },
  { id: 4, question: 'OASIS is missing tabs' },
  { id: 5, question: "I'm unable to create a plan of care from an OASIS assessment." },
  { id: 6, question: 'Tellus Setup' },
  { id: 7, question: 'What does the QA Status Code indicate on the Audit Trail Reports?' },
  { id: 8, question: 'What is OASIS Segue used for?' },
  { id: 9, question: 'Billing/Procedure Codes for Indiana EVV' },
  { id: 10, question: 'Telephony visits not matched to scheduled visit' },
];

const FAQPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedFAQ, setExpandedFAQ] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const toggleFAQ = (id) => {
    setExpandedFAQ(expandedFAQ === id ? null : id);
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <span className="inline-block bg-[#3498db] text-white text-xs font-semibold px-3 py-1 rounded mb-4">
            FAQ
          </span>
          <h1 className="text-2xl font-bold text-gray-800">FAQ</h1>
        </div>

        {/* Search Banner */}
        <div className="bg-gradient-to-r from-[#5a9fcf] to-[#7ab8d9] rounded-lg p-8 mb-6">
          <h2 className="text-2xl font-semibold text-white text-center mb-4">
            Frequently Asked Questions
          </h2>
          <div className="max-w-2xl mx-auto flex">
            <input
              type="text"
              placeholder="Enter your search term or question here to get started"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 px-4 py-3 rounded-l-lg border-0 focus:ring-2 focus:ring-[#3498db] focus:outline-none"
            />
            <button className="bg-[#7cb9a8] hover:bg-[#6aa897] text-white px-6 py-3 rounded-r-lg font-medium transition-colors">
              Go
            </button>
          </div>
        </div>

        <div className="flex gap-6">
          {/* Top 10 FAQ */}
          <div className="flex-1">
            <h3 className="text-xl font-semibold text-gray-700 text-center mb-4">Top 10 FAQ</h3>
            <div className="bg-white rounded-lg shadow-sm border">
              {topFAQs.map((faq) => (
                <div key={faq.id} className="border-b last:border-b-0">
                  <button
                    onClick={() => toggleFAQ(faq.id)}
                    className="w-full flex items-center justify-between px-4 py-3 hover:bg-gray-50 transition-colors text-left"
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-gray-500 font-medium">#{faq.id}</span>
                      <span className="text-gray-700">{faq.question}</span>
                    </div>
                    {expandedFAQ === faq.id ? (
                      <ChevronDown className="w-5 h-5 text-gray-400" />
                    ) : (
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    )}
                  </button>
                  {expandedFAQ === faq.id && (
                    <div className="px-4 py-3 bg-gray-50 text-gray-600 text-sm">
                      <p>Answer to "{faq.question}" will be displayed here. This is a placeholder for the actual FAQ content.</p>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Pagination */}
            <div className="flex justify-center items-center gap-1 mt-4">
              <button className="px-2 py-1 text-gray-400">«</button>
              {[1, 2, 3, 4, 5].map((page) => (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page)}
                  className={`px-3 py-1 rounded ${
                    currentPage === page
                      ? 'bg-[#3498db] text-white'
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {page}
                </button>
              ))}
              <span className="px-2 text-gray-400">...</span>
              <button className="px-3 py-1 text-gray-600 hover:bg-gray-100 rounded">25</button>
              <button className="px-2 py-1 text-gray-400">»</button>
            </div>
          </div>

          {/* Categories */}
          <div className="w-80">
            <h3 className="text-xl font-semibold text-gray-700 text-center mb-4">Categories</h3>
            <div className="bg-white rounded-lg shadow-sm border p-4">
              <div className="grid grid-cols-2 gap-2">
                {faqCategories.map((cat, idx) => (
                  <button
                    key={idx}
                    className="flex items-center gap-2 px-2 py-1.5 text-left text-sm text-[#3498db] hover:bg-gray-50 rounded transition-colors"
                  >
                    <span>{cat.icon}</span>
                    <span className="truncate">{cat.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center text-gray-500 text-sm mt-8 pt-4 border-t">
          © 2026 Ivory Healthcare Systems, LLC. <span className="float-right">Version: 8.61.4.19</span>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default FAQPage;
